import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import DateRangeIcon from "@mui/icons-material/DateRange";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import "./DateRangePicker.css";

const PRESETS = [
  { label: "Today", key: "today" },
  { label: "Yesterday", key: "yesterday" },
  { label: "Last 7 Days", key: "last7" },
  { label: "Last 30 Days", key: "last30" },
  { label: "This Month", key: "thisMonth" },
];

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const DateRangePicker = ({
  startDate,
  endDate,
  onApply,
  onClear,
  onClose,
}) => {
  const [start, setStart] = useState(
    startDate ? dayjs(startDate) : null
  );
  const [end, setEnd] = useState(
    endDate ? dayjs(endDate) : null
  );
  const [hoverDate, setHoverDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (startDate && dayjs(startDate).isValid()) {
      return dayjs(startDate).startOf("month");
    }
    return dayjs().startOf("month");
  });
  const [activePreset, setActivePreset] = useState(null);

  useEffect(() => {
    if (startDate) setStart(dayjs(startDate));
    if (endDate) setEnd(dayjs(endDate));
  }, [startDate, endDate]);

  const handlePreset = (key) => {
    const today = dayjs();
    let pStart, pEnd;

    if (key === "today") {
      pStart = today;
      pEnd = today;
    } else if (key === "yesterday") {
      pStart = today.subtract(1, "day");
      pEnd = today.subtract(1, "day");
    } else if (key === "last7") {
      pStart = today.subtract(6, "day");
      pEnd = today;
    } else if (key === "last30") {
      pStart = today.subtract(29, "day");
      pEnd = today;
    } else if (key === "thisMonth") {
      pStart = today.startOf("month");
      pEnd = today;
    }

    if (pStart && pEnd) {
      setStart(pStart);
      setEnd(pEnd);
      setHoverDate(null);
      setCurrentMonth(pStart.startOf("month"));
      setActivePreset(key);
    }
  };

  const handleDayClick = (dayObj) => {
    setActivePreset(null);

    // If no start date or range is already completed, start a new range
    if (!start || (start && end)) {
      setStart(dayObj);
      setEnd(null);
      setHoverDate(null);
      return;
    }

    // If start is set but end is not
    if (start && !end) {
      if (dayObj.isBefore(start, "day")) {
        // Clicked before start date, restart range with new date
        setStart(dayObj);
        setEnd(null);
      } else {
        // Complete the range
        setEnd(dayObj);
        setHoverDate(null);
      }
    }
  };

  const handleDayMouseEnter = (dayObj) => {
    if (start && !end) {
      setHoverDate(dayObj);
    }
  };

  const handleClear = () => {
    setStart(null);
    setEnd(null);
    setHoverDate(null);
    setActivePreset(null);
    if (onClear) {
      onClear();
    }
  };

  const handleApply = () => {
    if (!start) {
      handleClear();
      return;
    }
    // If only start is selected, treat it as a single date
    const finalStart = start;
    const finalEnd = end || start;
    onApply(finalStart, finalEnd);
  };

  const handlePrevMonth = () => {
    setCurrentMonth((prev) => prev.subtract(1, "month"));
  };

  const handleNextMonth = () => {
    setCurrentMonth((prev) => prev.add(1, "month"));
  };

  // Build calendar matrix
  const monthStart = currentMonth.startOf("month");
  const daysInMonth = currentMonth.daysInMonth();
  const startDayOfWeek = monthStart.day(); // 0 (Sun) to 6 (Sat)
  const prevMonth = currentMonth.subtract(1, "month");
  const daysInPrevMonth = prevMonth.daysInMonth();

  const calendarDays = [];

  // Trailing days from previous month
  for (let i = startDayOfWeek - 1; i >= 0; i--) {
    const day = prevMonth.date(daysInPrevMonth - i);
    calendarDays.push({ date: day, isCurrentMonth: false });
  }

  // Days in current month
  for (let d = 1; d <= daysInMonth; d++) {
    const day = currentMonth.date(d);
    calendarDays.push({ date: day, isCurrentMonth: true });
  }

  // Leading days from next month to complete rows (up to 35 or 42)
  const totalSlots = calendarDays.length <= 35 ? 35 : 42;
  const remainingSlots = totalSlots - calendarDays.length;
  const nextMonth = currentMonth.add(1, "month");
  for (let d = 1; d <= remainingSlots; d++) {
    const day = nextMonth.date(d);
    calendarDays.push({ date: day, isCurrentMonth: false });
  }

  // Display text for the single unified input
  const renderSingleInputText = () => {
    if (start && end) {
      const sFormatted = start.format("MMM DD, YYYY");
      const eFormatted = end.format("MMM DD, YYYY");
      return (
        <>
          <span>{sFormatted}</span>
          <span className="drp-date-arrow">→</span>
          <span>{eFormatted}</span>
        </>
      );
    }
    if (start && !end) {
      return (
        <>
          <span>{start.format("MMM DD, YYYY")}</span>
          <span className="drp-date-arrow">→</span>
          <span className="drp-date-placeholder">Click end date</span>
        </>
      );
    }
    return <span className="drp-date-placeholder">Select start & end date</span>;
  };

  return (
    <div className="drp-container">
      <div className="drp-header-title">Filter by Date Range</div>

      {/* Quick Presets */}
      <div className="drp-presets">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            className={`drp-preset-chip ${activePreset === p.key ? "active" : ""}`}
            onClick={() => handlePreset(p.key)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Single Unified Date Range Input */}
      <div className="drp-single-input">
        <div className="drp-input-text-container">
          <DateRangeIcon className="drp-input-icon" />
          {renderSingleInputText()}
        </div>
        {(start || end) && (
          <CloseIcon
            className="drp-clear-icon"
            onClick={(e) => {
              e.stopPropagation();
              setStart(null);
              setEnd(null);
              setHoverDate(null);
              setActivePreset(null);
            }}
            titleAccess="Clear dates"
          />
        )}
      </div>

      {/* Inline Interactive Calendar */}
      <div className="drp-calendar">
        {/* Month Navigation */}
        <div className="drp-calendar-nav">
          <button
            type="button"
            className="drp-nav-btn"
            onClick={handlePrevMonth}
            title="Previous month"
          >
            <ChevronLeftIcon fontSize="small" />
          </button>
          <span className="drp-month-label">
            {currentMonth.format("MMMM YYYY")}
          </span>
          <button
            type="button"
            className="drp-nav-btn"
            onClick={handleNextMonth}
            title="Next month"
          >
            <ChevronRightIcon fontSize="small" />
          </button>
        </div>

        {/* Weekdays Row */}
        <div className="drp-weekdays">
          {WEEKDAYS.map((wd) => (
            <div key={wd} className="drp-weekday">
              {wd}
            </div>
          ))}
        </div>

        {/* Days Grid */}
        <div
          className="drp-days-grid"
          onMouseLeave={() => setHoverDate(null)}
        >
          {calendarDays.map((item, idx) => {
            const { date, isCurrentMonth } = item;
            const isToday = date.isSame(dayjs(), "day");
            const isRangeStart = start && date.isSame(start, "day");
            const isRangeEnd = end && date.isSame(end, "day");

            const isInRange =
              start &&
              end &&
              date.isAfter(start, "day") &&
              date.isBefore(end, "day");

            const isHoverRange =
              start &&
              !end &&
              hoverDate &&
              hoverDate.isAfter(start, "day") &&
              date.isAfter(start, "day") &&
              (date.isBefore(hoverDate, "day") || date.isSame(hoverDate, "day"));

            let cellClasses = "drp-day-cell";
            if (!isCurrentMonth) cellClasses += " other-month";
            if (isToday) cellClasses += " today";
            if (isRangeStart) cellClasses += " range-start";
            if (isRangeEnd) cellClasses += " range-end";
            if (isInRange) cellClasses += " in-range";
            if (isHoverRange) cellClasses += " hover-range";

            return (
              <div
                key={idx}
                className={cellClasses}
                onClick={() => handleDayClick(date)}
                onMouseEnter={() => handleDayMouseEnter(date)}
              >
                <span className="drp-day-number">{date.date()}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer Controls */}
      <div className="drp-footer">
        <button
          type="button"
          className="drp-reset-btn"
          onClick={handleClear}
        >
          Reset
        </button>
        <div className="drp-action-group">
          <button
            type="button"
            className="drp-cancel-btn"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="drp-apply-btn"
            onClick={handleApply}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default DateRangePicker;
